import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useServerData } from '../hooks/useServerData';
import { useToast } from '../components/common/Toast';
import { LS_KEYS } from '../constants';
import { getDefaultFunds } from '../utils/storage';
import { getCurrency } from '../utils/helpers';
import { callClaude, fmtProfile, fmtCompactPortfolio, callClaudeIntelligence, callIntelligenceChat } from '../services/claudeApi';

const PortfolioContext = createContext(null);

export function PortfolioProvider({ advisorProfile, watchlistQuotes, portfolio, setPortfolio, children }) {
  const { toast } = useToast();

  // ── Portfolio scope (must be before Intelligence hooks that depend on it) ──
  const [portfolioScope, setPortfolioScope] = useState('personal');

  // ── KV-synced state ──
  const [personalFunds, setPersonalFunds] = useServerData('portfolio_funds', 'personal', getDefaultFunds(), LS_KEYS.FUNDS);
  const [sharedFunds, setSharedFunds] = useServerData('portfolio_funds', 'shared', [], null);
  const [intelligenceReport, setIntelligenceReport] = useServerData('intelligence_report', portfolioScope, null, portfolioScope === 'personal' ? LS_KEYS.INTELLIGENCE : null);
  const [intelligenceFrequency, setIntelligenceFrequency] = useServerData('intelligence_freq', portfolioScope, 'weekly', portfolioScope === 'personal' ? LS_KEYS.INTELLIGENCE_FREQ : null);
  const [intelligenceChat, setIntelligenceChat] = useServerData('intelligence_chat', portfolioScope, [], portfolioScope === 'personal' ? LS_KEYS.INTELLIGENCE_CHAT : null);
  const [recoveryData, setRecoveryData] = useServerData('recovery_data', 'personal', null, LS_KEYS.RECOVERY_DATA);
  const [riskCache, setRiskCache] = useServerData('risk_config', 'personal', null, LS_KEYS.RISK_CACHE);
  const [mcCache, setMcCache] = useServerData('monte_carlo', 'personal', null, LS_KEYS.MC_CACHE);

  // ── Local state ──
  const [newPos, setNewPos] = useState({ symbol: "", qty: "", entry: "", stopLoss: "" });
  const [guardianLoading, setGuardianLoading] = useState(false);
  const [guardianReport, setGuardianReport] = useState(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [intelligenceChatInput, setIntelligenceChatInput] = useState("");
  const [intelligenceChatLoading, setIntelligenceChatLoading] = useState(false);
  const intelligenceChatEndRef = useRef(null);

  // ── Derived ──
  const funds = portfolioScope === 'personal' ? personalFunds : sharedFunds;
  const setFunds = portfolioScope === 'personal' ? setPersonalFunds : setSharedFunds;

  // ── Fix broken stop-loss values (migrate old absolute -15 → proper % of entry) ──
  useEffect(() => {
    if (!portfolio || portfolio.length === 0) return;
    const defaultPct = advisorProfile?.stopLoss || 12;
    const fixed = portfolio.map(p => {
      if (p.stopLoss <= 0 || (p.entry > 0 && p.stopLoss < p.entry * 0.3)) {
        return { ...p, stopLoss: +(p.entry * (1 - defaultPct / 100)).toFixed(2) };
      }
      return p;
    });
    if (JSON.stringify(fixed) !== JSON.stringify(portfolio)) {
      setPortfolio(fixed);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scroll effect ──
  useEffect(() => { intelligenceChatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [intelligenceChat, intelligenceChatLoading]);

  // ── Callbacks ──
  const addPos = useCallback(() => {
    if (!newPos.symbol || !newPos.qty || !newPos.entry) return;
    const sym = newPos.symbol.toUpperCase().trim();
    const entry = parseFloat(newPos.entry);
    const slPct = newPos.stopLoss ? parseFloat(newPos.stopLoss) : (advisorProfile?.stopLoss || 12);
    const sl = +(entry * (1 - slPct / 100)).toFixed(2);
    setPortfolio(prev => [...prev, { id: Date.now(), symbol: sym, qty: parseFloat(newPos.qty), entry, stopLoss: sl }]);
    setNewPos({ symbol: "", qty: "", entry: "", stopLoss: "" });
    toast.success(`Posizione ${sym} aggiunta`);
  }, [newPos, advisorProfile, setPortfolio, toast]);

  const updatePos = useCallback((id, updates) => {
    setPortfolio(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
    toast.success('Posizione aggiornata');
  }, [setPortfolio, toast]);

  const removePos = useCallback((id) => {
    const pos = portfolio.find(p => p.id === id);
    setPortfolio(prev => prev.filter(p => p.id !== id));
    toast.info(`Posizione ${pos?.symbol || ''} rimossa`);
  }, [portfolio, setPortfolio, toast]);

  const runGuardian = useCallback(async () => {
    if (portfolio.length === 0) { setGuardianReport({ empty: true }); return; }
    setGuardianLoading(true);
    setGuardianReport(null);
    // Build position list with pre-calculated status (no interpretation needed from LLM)
    const posStr = portfolio.map(p => {
      const cur = getCurrency(p.symbol);
      const quote = watchlistQuotes?.[p.symbol];
      const livePrice = quote?.c;
      if (!livePrice) {
        return `- ${p.symbol}: ${p.qty} azioni @ ${cur}${p.entry} (entry), stop loss a ${cur}${p.stopLoss} | PREZZO LIVE NON DISPONIBILE`;
      }
      const pnlPct = ((livePrice - p.entry) / p.entry * 100);
      const pnlAbs = (livePrice - p.entry) * p.qty;
      const invested = p.qty * p.entry;
      const currentVal = p.qty * livePrice;
      const status = pnlPct > 0 ? `IN GUADAGNO +${pnlPct.toFixed(1)}%` : `IN PERDITA ${pnlPct.toFixed(1)}%`;
      const slDistance = ((livePrice - p.stopLoss) / livePrice * 100);
      const slStatus = slDistance < 5 ? `⚠️ STOP LOSS VICINO (${slDistance.toFixed(1)}% sopra SL)` : `stop loss lontano (${slDistance.toFixed(1)}% sopra SL)`;
      return `- ${p.symbol}: ${p.qty} azioni | Acquistate a ${cur}${p.entry} | Prezzo attuale ${cur}${livePrice.toFixed(2)} | ${status} (${cur}${pnlAbs.toFixed(0)} su ${cur}${invested.toFixed(0)} investiti → valore attuale ${cur}${currentVal.toFixed(0)}) | Stop loss fisso a ${cur}${p.stopLoss} | ${slStatus}`;
    }).join("\n");
    const msg = `Sei un Portfolio Guardian. Analizza le posizioni sotto. I dati sono GIÀ CALCOLATI — NON ricalcolare P&L o stato SL, usali direttamente.

REGOLE CRITICHE:
- "IN GUADAGNO" = posizione positiva, il prezzo attuale è SOPRA l'entry
- "IN PERDITA" = posizione negativa, il prezzo attuale è SOTTO l'entry
- Lo stop loss è SEMPRE sotto l'entry (è normale, protegge dalle perdite). NON dire mai "SL superiore all'entry" o "SL violato" a meno che il prezzo attuale sia effettivamente ≤ SL
- "STOP LOSS VICINO" è già marcato nei dati — se non lo vedi, NON dire che lo stop è vicino

Per ogni titolo:
1. Leggi i dati pre-calcolati (stato, P&L, distanza SL)
2. Cerca news recenti con web_search per contestualizzare
3. Suggerisci azioni solo se davvero necessario

POSIZIONI:
${posStr}

Formato output:
═══════════════════════════════════════
  PORTFOLIO CHECK — Oggi
═══════════════════════════════════════
📊 RIEPILOGO
| Simbolo | Prezzo attuale | Entry | P&L% | Stato | Commento |
|---------|---------------|-------|------|-------|----------|

🔴 ALERT CRITICI
[SOLO se: SL VICINO marcato nei dati, perdita >15%, o news molto negative. Se nessuna, scrivi "Nessuno"]

🟡 ATTENZIONE
[Se presenti considerazioni di medio rilievo. Se nessuna, scrivi "Nessuna"]

🟢 POSIZIONI OK
[Lista posizioni senza problemi]

💡 AZIONI SUGGERITE
[1-3 azioni concrete. Se le posizioni sono tutte OK, dillo esplicitamente: "Portfolio in salute, nessuna azione urgente"]

⚠️ TRAILING STOP
[Per posizioni in guadagno >10%, suggerisci di valutare l'aggiornamento dello stop loss per proteggere il profitto accumulato]
═══════════════════════════════════════`;
    const result = await callClaude([{ role: "user", content: msg }], fmtProfile(advisorProfile), fmtCompactPortfolio(portfolio, funds, watchlistQuotes));
    setGuardianReport({ text: result.error ? `❌ Errore: ${result.error}` : result.text });
    if (result.error) toast.error('Errore nel Portfolio Check');
    else toast.success('Portfolio Check completato');
    setGuardianLoading(false);
  }, [portfolio, funds, advisorProfile, watchlistQuotes, toast]);

  const runIntelligence = useCallback(async () => {
    if (funds.length === 0 && portfolio.length === 0) return false;
    setIntelligenceLoading(true);
    const result = await callClaudeIntelligence(fmtProfile(advisorProfile), portfolio, funds, intelligenceReport, watchlistQuotes);
    const report = {
      text: result.error ? `❌ Errore: ${result.error}` : result.text,
      date: new Date().toISOString(),
      frequency: intelligenceFrequency,
      fallbackModel: result.fallbackModel || null
    };
    setIntelligenceReport(report);
    if (result.recoveryData) setRecoveryData(result.recoveryData);
    setIntelligenceChat([], { immediate: true }); // Reset chat on new report — flush KV immediately
    if (result.error) toast.error('Errore nel report Intelligence');
    else if (result.fallbackModel) toast.success('Report generato con modello alternativo (rate limit)');
    else toast.success('Intelligence Report aggiornato');
    setIntelligenceLoading(false);
    return !result.error;
  }, [portfolio, funds, advisorProfile, intelligenceFrequency, intelligenceReport, watchlistQuotes, toast, setIntelligenceReport, setRecoveryData, setIntelligenceChat]);

  const sendIntelligenceMsg = useCallback(async (directMsg) => {
    const q = (directMsg || intelligenceChatInput).trim();
    if (!q || intelligenceChatLoading) return;
    setIntelligenceChatInput("");
    setIntelligenceChat(prev => [...prev, { role: "user", text: q }].slice(-20));
    setIntelligenceChatLoading(true);
    const history = intelligenceChat.slice(-10).map(m => ({ role: m.role === "user" ? "user" : "assistant", content: m.text }));
    history.push({ role: "user", content: q });
    const result = await callIntelligenceChat(history, fmtProfile(advisorProfile), portfolio, funds, intelligenceReport?.text, watchlistQuotes);
    if (result.error) toast.error('Errore nella chat Intelligence');
    setIntelligenceChat(prev => [...prev, { role: "assistant", text: result.error ? `❌ Errore: ${result.error}` : result.text }].slice(-20));
    setIntelligenceChatLoading(false);
  }, [intelligenceChatInput, intelligenceChatLoading, intelligenceChat, advisorProfile, portfolio, funds, intelligenceReport, watchlistQuotes, toast, setIntelligenceChat]);

  // ── Auto-run Intelligence Agent based on frequency (only personal scope) ──
  useEffect(() => {
    if (portfolioScope !== 'personal') return; // Don't auto-run for shared scope
    if (!intelligenceReport?.date || intelligenceLoading) return;
    const lastRun = new Date(intelligenceReport.date);
    const now = new Date();
    const daysDiff = (now - lastRun) / (1000 * 60 * 60 * 24);
    const thresholds = { daily: 1, weekly: 7, monthly: 30 };
    const threshold = thresholds[intelligenceFrequency] || 7;
    if (daysDiff >= threshold && (funds.length > 0 || portfolio.length > 0)) {
      runIntelligence();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Context value ──
  const value = useMemo(() => ({
    // positions
    portfolio, newPos, setNewPos, addPos, updatePos, removePos,
    // funds
    funds, setFunds, portfolioScope, setPortfolioScope,
    // advisor (pass-through)
    advisorProfile,
    // guardian
    guardianLoading, guardianReport, runGuardian,
    // intelligence
    intelligenceReport, intelligenceLoading, intelligenceFrequency, setIntelligenceFrequency,
    runIntelligence, intelligenceChat, intelligenceChatInput, setIntelligenceChatInput,
    intelligenceChatLoading, sendIntelligenceMsg, intelligenceChatEndRef,
    // data
    watchlistQuotes, recoveryData,
    // caches
    riskCache, setRiskCache, mcCache, setMcCache,
  }), [
    portfolio, newPos, addPos, removePos,
    funds, setFunds, portfolioScope,
    advisorProfile,
    guardianLoading, guardianReport, runGuardian,
    intelligenceReport, intelligenceLoading, intelligenceFrequency, setIntelligenceFrequency,
    runIntelligence, intelligenceChat, intelligenceChatInput,
    intelligenceChatLoading, sendIntelligenceMsg,
    watchlistQuotes, recoveryData,
    riskCache, setRiskCache, mcCache, setMcCache,
  ]);

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
}

export function usePortfolio() {
  const ctx = useContext(PortfolioContext);
  if (!ctx) throw new Error('usePortfolio must be inside PortfolioProvider');
  return ctx;
}
